import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  Output,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CampaignStatus, OnsiteCampaign, OnsiteCampaignType, TargetDevice } from '../../campaign/campaign.model';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { CampaignService } from 'app/shared/services/campaign.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { ActivatedRoute } from '@angular/router';
import { WidgetState } from 'app/campaign/widgets.model';
import { WidgetService } from 'app/shared/services/widget.service';
import { CurrentContextService } from 'app/core/current-context.service';

@Component({
  selector: 'app-campaign-breadcrumb-bar',
  templateUrl: './campaign-breadcrumb-bar.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./campaign-breadcrumb-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CampaignBreadcrumbBarComponent implements OnInit, OnDestroy {
  @Output() public toSettingPath = new EventEmitter();
  @Input() public currentItemTextBS: BehaviorSubject<string>;
  @Input() public currentItemTypeBS: BehaviorSubject<OnsiteCampaignType>;
  public readonly onsiteExperiencesLink = '/onsite-experiences';
  @Input() public hasUnpublishedChanges: boolean;
  @Input() public campaign: OnsiteCampaign;
  @Input() public campaignId: number;
  private currentItemTextSubscription: Subscription;
  private currentItemTypeSubscription: Subscription;
  private widgetStateSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  public currentItemText: string;
  public accountName: string;
  public currentItemType: string;
  public CampaignTypes = OnsiteCampaignType;

  public get isAppExperience(): boolean {
    return this.campaign?.targetDevice === TargetDevice.NativeApp;
  }

  public get showOnsiteExperiencesLink(): boolean {
    return !this.isMobile && !!this.campaign && !this.isAppExperience;
  }

  public OnsiteCampaignStatuses = CampaignStatus;
  public isChangingName = false;
  public campaignName: string;
  public variantName: string;
  public variantId: number;
  public widgetState: WidgetState;
  public isChangingVariant = false;

  @ViewChild('nameEdit') nameEdit: ElementRef;
  @ViewChild('variantEdit') variantEdit: ElementRef;

  @ViewChild('invisibleName') invisibleName: ElementRef;
  @ViewChild('invisibleVariant') invisibleVariant: ElementRef;

  public nameWidth: number;
  public variantWidth: number;

  public isMobile = false;

  @HostListener('window:resize')
  getScreenHeight(): void {
    this.isMobile = window.innerWidth <= 676;
  }

  constructor(
    private readonly currentContextService: CurrentContextService,
    private readonly campaignService: CampaignService,
    private readonly widgetService: WidgetService,
    private readonly snackbarService: SnackBarService,
    private route: ActivatedRoute,
    private readonly widgetEditorDataService: WidgetEditorDataService,
  ) {}

  ngOnInit() {
    this.currentContextService.currentContextBS.subscribe((currentContext) => {
      if (currentContext) {
        this.accountName = currentContext.account.name;
        this.getScreenHeight();
      }
    });
    this.currentItemTextSubscription = this.currentItemTextBS.subscribe((text: string) => {
      this.currentItemText = text;
      this.campaignName = text;
    });
    this.currentItemTypeSubscription = this.currentItemTypeBS.subscribe((type: OnsiteCampaignType) => {
      this.currentItemType = type;
    });

    this.route.paramMap.subscribe((params) => {
      if (params.get('campaignId')) {
        this.campaignId = Number(params.get('campaignId'));
      }
      if (params.get('widgetId')) {
        this.variantId = Number(params.get('widgetId'));
      }
    });
    this.widgetStateSubscription = this.widgetEditorDataService.widgetBS.subscribe((widget: any) => {
      if (widget) {
        this.widgetState = widget;
        this.variantName = widget.name;
      }
    });
  }
  focusOnFirstName() {
    this.nameEdit.nativeElement.focus();
  }
  focusOnVariantEdit() {
    this.variantEdit.nativeElement.focus();
  }

  resizeInputName() {
    setTimeout(() => {
      const minWidth = 100;
      this.nameWidth = Math.max(this.invisibleName.nativeElement.offsetWidth, minWidth);
    }, 0);
  }
  public resizeInputVariant() {
    setTimeout(() => {
      const minWidth = 100;
      this.variantWidth = Math.max(this.invisibleVariant.nativeElement.offsetWidth, minWidth);
    }, 0);
  }

  protected get settingsHref(): string | null {
    if (!this.campaign) {
      return null;
    }
    const prefix = this.campaign.type === OnsiteCampaignType.ABTesting ? '/campaigns/ab-tests' : '/campaigns';
    const suffix = this.campaign.status === CampaignStatus.Draft ? '/settings/wizard' : '/settings';
    return `${prefix}/${this.campaign.id}${suffix}`;
  }

  public onCampaignNameClick(event: MouseEvent): void {
    // Modified clicks follow the href so the browser can open a new tab.
    if (this.settingsHref && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) {
      return;
    }
    event.preventDefault();
    this.toSettingPath.emit();
  }

  public changeName() {
    this.isChangingName = true;
    this.resizeInputName();
    setTimeout(() => {
      this.focusOnFirstName();
    }, 200);
  }

  public changeVariant() {
    this.isChangingVariant = true;
    this.resizeInputVariant();
    setTimeout(() => {
      this.focusOnVariantEdit();
    }, 200);
  }

  public saveChangedName() {
    this.isChangingName = false;
    if (this.campaignName !== null && this.campaignName !== '') {
      this.campaignService
        .updateCampaignWithPatch(
          this.campaign,
          this.campaignService.getPatchOperations({ id: this.campaignId, name: this.campaignName } as OnsiteCampaign),
        )
        .subscribe({
          next: (campaign: OnsiteCampaign) => {
            this.snackbarService.success('campaign.settings.basic.name-updated');
            this.currentItemText = this.campaignName;
            campaign.name = this.currentItemText;
          },
          error: () => {
            this.snackbarService.error('campaign.settings.basic.name-update-failed');
          },
        });
    }
  }
  public saveChangedVariant() {
    this.isChangingVariant = false;
    if (this.variantName !== null && this.variantName !== '') {
      this.widgetService.updateWidgetName(this.variantId, this.variantName).subscribe(
        () => {
          this.snackbarService.success('campaign.settings.basic.name-updated');
          this.widgetState.name = this.variantName;
        },
        () => {
          this.snackbarService.error('campaign.settings.basic.name-update-failed');
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(
      this.currentItemTextSubscription,
      this.currentItemTypeSubscription,
      this.widgetStateSubscription,
    );
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
